import { MANAGER_IMPACT_REPORT_ACCESS } from "@domains/manager-impact/shared/managerImpactAccess.ts";
import { initManagerImpactI18next } from "@domains/manager-impact/client/i18n/i18next.ts";
import { initActionItemsI18next } from "@domains/action-items/client/i18n/i18next.ts";
import { initConnectedAppsI18next } from "@domains/connected-apps/client/i18n/i18next.ts";
import { initIntentBoxI18next } from "@domains/intent-box/client/i18n/i18next.ts";
import { initFeedbackI18next } from "@domains/feedback/client/i18n/i18next.ts";
import { initMeetingsI18next } from "@domains/meetings/client/i18n/i18next.ts";
import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import { initPlansI18next } from "@domains/plans/client/i18n/i18next.ts";
import { ChartBarIcon, ChatBubbleLeftRightIcon, HeartIcon, HomeIcon } from "@heroicons/react/24/outline";
import { withFeature } from "@packages/core/access/featureAccess.ts";
import { hasFeature } from "@packages/core/session/features.ts";
import { hasProduct } from "@packages/core/session/products.ts";
import { initScopeI18next } from "@packages/core-client/scope/i18n/i18next.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerCreateMenuItem } from "@packages/core-client/registration/createMenu.tsx";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import type { FireflyRuntime } from "@squide/firefly";
import { initI18next } from "./i18n/i18next.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const [{ getRequestHandlers }, { homeAgentRequestHandlers }] = await Promise.all([import("./api/mocks/getRequestHandlers.ts"), import("./api/mocks/assistant/requestHandlers.ts")]);

        runtime.registerRequestHandlers(getRequestHandlers());
        runtime.registerRequestHandlers(homeAgentRequestHandlers, { prepend: true });
    }
}

export const registerHome: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initScopeI18next(runtime);
    initMeetingsI18next(runtime);
    initFeedbackI18next(runtime);
    initReviewCyclesI18next(runtime);
    initConnectedAppsI18next(runtime);
    initPlansI18next(runtime);
    initActionItemsI18next(runtime);
    initManagerImpactI18next(runtime);
    initIntentBoxI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "home", "/", HomeIcon, i18nextInstance);

    area.registerPage({
        page: "Home",
        lazy: async () => {
            const { HomePage } = await import("./pages/HomePage.tsx");

            return { element: <HomePage /> };
        }
    });

    area.registerToTopNav({ priority: 300 });

    const managerImpactArea = registerArea(runtime, "manager-impact", "/manager-impact", ChartBarIcon, i18nextInstance, { access: withFeature("Engagement", MANAGER_IMPACT_REPORT_ACCESS) });

    managerImpactArea.registerPage({
        page: "ManagerImpactReport",
        lazy: async () => {
            const { ManagerImpactReportPage } = await import("@domains/manager-impact/client/components/ManagerImpactReportPage.tsx");

            return { element: <ManagerImpactReportPage /> };
        }
    });

    return (_deferredRuntime, { session }: AppDeferredRegistrationData) => {
        if (!hasProduct(session, "wov")) {
            return;
        }

        if (hasFeature("Recognition")(session)) {
            registerCreateMenuItem(runtime, {
                id: "good-vibe",
                label: { instance: i18nextInstance, namespace: "CreateMenu", resourceKey: "newGoodVibe" },
                icon: HeartIcon,
                to: "/?compose=good-vibe",
                priority: 50
            });
        }

        if (hasFeature("Engagement")(session)) {
            registerCreateMenuItem(runtime, {
                id: "suggestion",
                label: { instance: i18nextInstance, namespace: "CreateMenu", resourceKey: "newFeedback" },
                icon: ChatBubbleLeftRightIcon,
                to: "/?compose=suggestion",
                priority: 40
            });
        }
    };
};
